import classNames from "classnames"

import ImageLink from "@components/elements/ImageLink"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import NumberCallout, {
  NumberCalloutProps,
} from "@components/elements/V2/NumberCallout"
import PriceBadge, { PriceBadgeProps } from "@components/elements/V2/priceBadge"
import Section from "@components/elements/V2/Section"
import TrustedStamp from "@components/elements/V2/TrustedStamp"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/elements/V2/Video"
import HeroLayout from "@components/V2/Heroes/HeroLayout"
import { Color } from "@constants/V2/color"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React from "react"

type Props = {
  headingSuperscript?: string
  heading?: string
  headingSize?: "medium" | "large"
  description?: string
  highlightedDescriptionText?: string
  imageUrl: string
  imageAlt: string
  videoUrl?: string
  buttons?: {
    text: string
    link?: string | YocoCom.YocoLink
    imageUrl?: string
    imageAlt?: string
    imageButtonDescription?: string
  }[]
  showTrustedStamp?: boolean
  backgroundColor?: Color
  numberCallout?: NumberCalloutProps
  priceBadge?: PriceBadgeProps
}

const ThirdsHero = ({
  headingSuperscript,
  heading,
  headingSize = "large",
  description,
  highlightedDescriptionText,
  imageUrl,
  imageAlt,
  videoUrl,
  buttons,
  showTrustedStamp = false,
  numberCallout,
  priceBadge,
  backgroundColor = Color.White,
  ...props
}: Props) => {
  const accentColor = getAccentColor(backgroundColor)
  return (
    <HeroLayout>
      <Section
        className={classNames(
          "grid grid-cols-12 pt-12 sm:pb-40 md:pt-navHeight-md",
          getBackgroundColorClass(backgroundColor)
        )}
        {...props}
      >
        <div className="col-span-12 my-auto flex flex-col gap-10 md:col-span-5 md:pr-[52px]">
          <Typography
            text={(headingSuperscript ?? "").toLocaleUpperCase()}
            size="body-sm"
            font="grotesk"
            weight="book"
            color={accentColor}
          />

          <Typography
            text={heading ?? ""}
            size={headingSize === "large" ? "h4" : "h5"}
            font="grotesk"
            weight="medium"
            color={accentColor}
          />

          <Typography
            text={description ?? ""}
            size="body-md"
            font="grotesk"
            weight="book"
            color={accentColor}
            highlightedText={highlightedDescriptionText}
            highlightColor={accentColor}
            highlightWeight="medium"
          />

          {priceBadge?.text && (
            <PriceBadge
              text={priceBadge.text}
              highlight={priceBadge.highlight}
              price={priceBadge.price}
              backgroundColor={priceBadge.backgroundColor}
              classname="-mb-24"
            />
          )}

          <div className="my-24 flex w-full flex-row flex-wrap gap-16 md:mt-32">
            {buttons?.map((button, index) => {
              if (button.link && button.text) {
                if (button.imageUrl) {
                  return (
                    <ImageLink
                      key={`link-${button.text}`}
                      size="medium"
                      link={button.link}
                      imageUrl={button.imageUrl}
                      imageAlt={button.text}
                      text={button.text}
                      description={button.imageButtonDescription}
                    />
                  )
                }

                const isFirst = index === buttons.findIndex((b) => !b.imageUrl)
                return (
                  <PillButton
                    key={button.text}
                    text={button.text}
                    style={isFirst ? "solid" : "bordered"}
                    size="medium"
                    color={Color.Blue}
                    overrideTextColor={isFirst ? undefined : Color.Black}
                    linkUrl={button.link}
                    className="hidden w-auto sm:!w-[204px] md:flex"
                  />
                )
              }
            })}
          </div>

          {numberCallout ? (
            <NumberCallout
              icon={numberCallout.icon}
              text={numberCallout.text}
              amount={numberCallout.amount}
              amountPrefix={numberCallout.amountPrefix}
              amountSuffix={numberCallout.amountSuffix}
              isAnimated={numberCallout.isAnimated}
              textColor={accentColor}
            />
          ) : (
            showTrustedStamp && <TrustedStamp />
          )}
        </div>

        <div className="col-span-12 my-[36px] md:col-span-7 md:my-0">
          {videoUrl ? (
            <Video
              video={videoUrl}
              coverImage={imageUrl}
              ImageAlt={imageAlt}
              loadType="eager"
              aspectRatio="4:3"
              playbackMode="autoplay"
              playOnMobile
              className="rounded-12"
            />
          ) : (
            <GatsbyStoryblokImage
              image={imageUrl}
              alt={imageAlt}
              loadType="eager"
              fetchPriority="high"
              placeholderColor={backgroundColor}
              quality={80}
              aspectRatio="fullWidth"
              rounded
              className="h-full w-auto"
            />
          )}
        </div>

        {buttons?.map((button, index) => {
          if (button.link && button.text) {
            if (!button.imageUrl) {
              const isFirst = index === buttons.findIndex((b) => !b.imageUrl)
              return (
                <PillButton
                  key={button.text}
                  text={button.text}
                  style={isFirst ? "solid" : "bordered"}
                  size="medium"
                  color={Color.Blue}
                  overrideTextColor={isFirst ? undefined : Color.Black}
                  linkUrl={button.link}
                  className="col-span-12 mb-24 flex w-full sm:!w-[204px] md:hidden"
                />
              )
            }
          }
        })}
      </Section>
    </HeroLayout>
  )
}

export default ThirdsHero
